// src/pages/orders/components/AirwaybillTrackingModal.jsx
import React, { useEffect, useState } from 'react';
import { X, Truck, Loader2, AlertCircle, PackageSearch, Copy, Check, Clock } from 'lucide-react';
import { getOrderCourierTracking } from '../../../services/api/orders';
import { useLocalization } from '../../../contexts/LocalizationContext';
import styles from './AirwaybillTrackingModal.module.css';

// Cycled per timeline step purely for visual variety — not tied to any status meaning,
// since courier APIs only return { date, note } with no status/type field to key off.
const TIMELINE_STEP_COLORS = [
  { accent: '#7F5CFF', bg: 'rgba(127, 92, 255, 0.09)' },
  { accent: '#3b82f6', bg: 'rgba(59, 130, 246, 0.09)' },
  { accent: '#10b981', bg: 'rgba(16, 185, 129, 0.09)' },
  { accent: '#f59e0b', bg: 'rgba(245, 158, 11, 0.09)' },
  { accent: '#ec4899', bg: 'rgba(236, 72, 153, 0.09)' },
  { accent: '#06b6d4', bg: 'rgba(6, 182, 212, 0.09)' },
  { accent: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.09)' },
  { accent: '#ef4444', bg: 'rgba(239, 68, 68, 0.09)' },
];

const AirwaybillTrackingModal = ({ orderId, airwaybillNumber, courierName, onClose }) => {
  const { t } = useLocalization();

  const [trackingData, setTrackingData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadTracking = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        const response = await getOrderCourierTracking(orderId);
        if (!cancelled) setTrackingData(response.data);
      } catch (err) {
        console.error('Failed to load courier tracking:', err);
        if (!cancelled) {
          setLoadError(err.response?.data?.message || t('ecomOrders.failedToLoadCourierTracking'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadTracking();
    return () => { cancelled = true; };
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(airwaybillNumber);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const timeline = trackingData?.packageTimeline || [];
  const stepCount = timeline.length;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerInfo}>
            <div className={styles.headerIconWrap}>
              <Truck size={18} />
            </div>
            <div>
              <h2>{t('ecomOrders.packageTimeline')}</h2>
              <div className={styles.awbRow}>
                <span className={styles.awbNumber}>{airwaybillNumber}</span>
                <button className={styles.copyBtn} onClick={handleCopy} title={isCopied ? 'Copied!' : 'Copy airwaybill number'}>
                  {isCopied ? <Check size={13} /> : <Copy size={13} />}
                </button>
                {courierName && <span className={styles.courierTag}>{courierName}</span>}
              </div>
            </div>
          </div>
          <button className={styles.closeBtn} onClick={onClose} title={t('common.close')}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {isLoading ? (
            <div className={styles.centerState}>
              <Loader2 size={28} className={styles.spinner} />
              <p>{t('ecomOrders.loadingTracking')}</p>
            </div>
          ) : loadError ? (
            <div className={styles.centerState}>
              <AlertCircle size={28} className={styles.errorIcon} />
              <p>{loadError}</p>
            </div>
          ) : (
            <>
              {timeline.length > 0 ? (
                <div className={styles.timeline}>
                  {timeline.map((event, idx) => {
                    const { accent, bg } = TIMELINE_STEP_COLORS[idx % TIMELINE_STEP_COLORS.length];
                    const isLatest = idx === 0;
                    const isLast = idx === timeline.length - 1;

                    return (
                      <div key={idx} className={styles.timelineItem}>
                        <div className={styles.timelineRail}>
                          <div className={styles.timelineDot} style={{ background: accent }}>
                            {stepCount - idx}
                          </div>
                          {!isLast && <div className={styles.timelineConnector} />}
                        </div>
                        <div
                          className={`${styles.timelineCard} ${isLatest ? styles.timelineCardLatest : ''}`}
                          style={{ '--accent': accent, '--accentBg': bg }}
                        >
                          <div className={styles.timelineNote}>
                            <span>{event.note || t('common.na')}</span>
                            {isLatest && <span className={styles.latestBadge}>{t('ecomOrders.latest')}</span>}
                          </div>
                          <div className={styles.timelineDate}>
                            <Clock size={11} />
                            {event.date || t('common.na')}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className={styles.emptyState}>
                  <PackageSearch size={32} />
                  <p>{t('ecomOrders.noTrackingEvents')}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AirwaybillTrackingModal;
